
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { normalizeDescription } from "@/utils/contentUtils";
import { useAuth } from "@/contexts/AuthContext";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface UseEditableContentProps {
  contentItem: {
    id: string;
    title: string;
    description?: string;
  };
  refetchContent?: () => void;
}

export const useEditableContent = ({ contentItem, refetchContent }: UseEditableContentProps) => {
  const { user } = useAuth();
  const [title, setTitle] = useState(contentItem.title || '');
  const [description, setDescription] = useState(normalizeDescription(contentItem.description));
  const [isLoading, setIsLoading] = useState(false);
  const contentRef = useRef(contentItem);
  const isInitialMount = useRef(true);
  
  // Only update local state when contentItem ID changes or on first mount
  useEffect(() => {
    if (isInitialMount.current || contentItem.id !== contentRef.current.id) {
      console.log("[useEditableContent] Initial mount or content ID changed, updating state:", {
        id: contentItem.id,
        title: contentItem.title,
        description: normalizeDescription(contentItem.description)
      });
      
      setTitle(contentItem.title || '');
      setDescription(normalizeDescription(contentItem.description));
      contentRef.current = contentItem;
      isInitialMount.current = false;
    }
  }, [contentItem, contentItem.id]);

  const saveTitle = async (): Promise<boolean> => {
    // Skip if no changes
    if (title === contentRef.current.title) {
      console.log("[useEditableContent] No changes to title, skipping save");
      return true;
    }
    
    console.log("[useEditableContent] === STARTING TITLE SAVE PROCESS ===");
    console.log("[useEditableContent] Saving title:", {
      id: contentItem.id,
      original: contentRef.current.title,
      new: title
    });
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("content_items")
        .update({ title })
        .eq("id", contentItem.id);
      
      if (error) {
        console.error("[useEditableContent] Error updating title:", error);
        toast.error("Failed to update title");
        // Don't reset to maintain user's changes for retry
        setIsLoading(false);
        return false;
      }
      
      console.log("[useEditableContent] === TITLE UPDATED SUCCESSFULLY ===");
      console.log("[useEditableContent] About to call analytics tracking...");
      
      // ANALYTICS: Track content editing for title
      try {
        console.log(`[useEditableContent] Calling Tier1Tracking.trackContentEdited for title...`);
        console.log(`[useEditableContent] Parameters:`, {
          contentId: contentItem.id,
          fieldsChanged: ['title'],
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentEdited(contentItem.id, ['title'], user);
        console.log(`[useEditableContent] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error('[useEditableContent] === ANALYTICS TRACKING FAILED ===');
        console.error('[useEditableContent] Analytics error:', analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      toast.success("Title updated");
      
      // Update our reference
      contentRef.current = {
        ...contentRef.current,
        title
      };
      
      if (refetchContent) {
        console.log("[useEditableContent] Refreshing content after title update");
        refetchContent();
      }
      
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error("[useEditableContent] Exception when updating title:", err);
      toast.error("Failed to update title");
      setIsLoading(false);
      return false;
    }
  };

  const saveDescription = async (): Promise<boolean> => {
    const normalizedDescription = normalizeDescription(description);
    const originalNormalizedDescription = normalizeDescription(contentRef.current.description);
    
    // Skip if no changes
    if (normalizedDescription === originalNormalizedDescription) {
      console.log("[useEditableContent] No changes to description, skipping save");
      return true;
    }
    
    console.log("[useEditableContent] === STARTING DESCRIPTION SAVE PROCESS ===");
    console.log("[useEditableContent] Saving description:", {
      id: contentItem.id, 
      original: contentRef.current.description,
      normalizedOriginal: originalNormalizedDescription,
      new: normalizedDescription
    });
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("content_items")
        .update({ description: normalizedDescription })
        .eq("id", contentItem.id);
      
      if (error) {
        console.error("[useEditableContent] Error updating description:", error);
        toast.error("Failed to update description");
        // Don't reset to maintain user's changes for retry
        setIsLoading(false);
        return false;
      }
      
      console.log("[useEditableContent] === DESCRIPTION UPDATED SUCCESSFULLY ===");
      console.log("[useEditableContent] About to call analytics tracking...");
      
      // ANALYTICS: Track content editing for description
      try {
        console.log(`[useEditableContent] Calling Tier1Tracking.trackContentEdited for description...`);
        console.log(`[useEditableContent] Parameters:`, {
          contentId: contentItem.id,
          fieldsChanged: ['description'],
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentEdited(contentItem.id, ['description'], user);
        console.log(`[useEditableContent] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error('[useEditableContent] === ANALYTICS TRACKING FAILED ===');
        console.error('[useEditableContent] Analytics error:', analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      toast.success("Description updated");
      
      // Update our reference
      contentRef.current = {
        ...contentRef.current,
        description: normalizedDescription
      };
      
      if (refetchContent) {
        console.log("[useEditableContent] Refreshing content after description update");
        refetchContent();
      }
      
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error("[useEditableContent] Exception when updating description:", err);
      toast.error("Failed to update description");
      setIsLoading(false);
      return false;
    }
  };

  const resetToOriginal = () => {
    setTitle(contentRef.current.title);
    setDescription(normalizeDescription(contentRef.current.description));
  };

  return {
    title,
    description,
    setTitle,
    setDescription,
    saveTitle,
    saveDescription,
    resetToOriginal,
    isLoading
  };
};

export default useEditableContent;
